// =============================================================================
// Trips page (tenant-admin scope)
//
// Lists every trip a tenant's users have created, plus a foldable
// "Top Destinations" rollup that surfaces where the user base is
// planning to travel. Click any destination in the rollup → filters
// the trips table beneath. Default scope is "Planned + Live" trips
// over the last 7 days; both can be widened from the UI.
//
// Type column on the table distinguishes Planned (AI-generated) from
// Imported trips (PDF / URL / Word / Paste) using the trip's
// source_type column. URL imports also get a small link-out chip
// that opens the original source_url.
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import {
  MapPin, Calendar, Users, Search, RefreshCw, ChevronDown, ChevronRight,
  X, ExternalLink, Sparkles, Upload,
} from 'lucide-react';
import { StatCard, Spinner } from '@/components/shared/UIComponents';
import { fetchTenantTrips } from '@/lib/api';
import type { AppTenantTrip } from '@/lib/api';
import type { Tenant } from '@/types';

// ── Status filter scope ──────────────────────────────────────────────
// "Planned + Live" per product spec: in-flight or upcoming trips that
// matter for ops + content planning. Excludes archived / completed
// because those are historical — the rollup is about where users are
// CURRENTLY planning to go. The admin can flip the per-status filter
// to widen if they want.
const PLANNED_AND_LIVE = new Set(['active', 'planning', 'saved', 'paused']);

// ── Time-window options ──────────────────────────────────────────────
// Days windowed back from "now"; null means custom (uses customFrom +
// customTo state). Default is '7' — most useful for active operations
// teams who want to know "what's hot this week."
type TimeWindowKey = '7' | '14' | '30' | '90' | '365' | 'custom';
const WINDOW_LABEL: Record<TimeWindowKey, string> = {
  '7': '7 days', '14': '14 days', '30': '30 days', '90': '90 days', '365': '1 year', 'custom': 'Custom',
};

// ── Destination normalization ────────────────────────────────────────
// Same destinations get written different ways across trips: "Paris"
// vs "Paris, France" vs "paris" vs "Paris, France 🇫🇷". Without
// normalization the rollup fragments and "Paris" hides under 4 keys.
// Lowercase + diacritic strip + ", country" suffix strip handles 95%
// of fragmentation; an alias map covers the native↔English pairs we
// see in European city names.
const CITY_ALIASES: Record<string, string> = {
  roma:     'rome',     wien:    'vienna',   munchen:  'munich',
  firenze:  'florence', venezia: 'venice',   napoli:   'naples',
  milano:   'milan',    praha:   'prague',   lisboa:   'lisbon',
  sevilla:  'seville',  koln:    'cologne',  beograd:  'belgrade',
  moskva:   'moscow',   bruxelles: 'brussels',
};
function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
function splitDestination(raw: string): { city: string; country: string | null } {
  // Take everything before the first comma as the city, anything after
  // as the country. "Paris, France" → {city:'Paris', country:'France'}.
  // "Tokyo" → {city:'Tokyo', country:null}. Country is left as-typed
  // (capitalisation preserved) so it renders cleanly without re-
  // capitalising it back from lowercased keys.
  const trimmed = raw.trim();
  if (!trimmed) return { city: '', country: null };
  const commaIdx = trimmed.indexOf(',');
  if (commaIdx < 0) return { city: trimmed, country: null };
  return {
    city:    trimmed.slice(0, commaIdx).trim(),
    country: trimmed.slice(commaIdx + 1).trim() || null,
  };
}
function normCity(raw: string): string {
  const { city } = splitDestination(raw);
  const base = stripDiacritics(city.toLowerCase()).replace(/\s+/g, ' ').trim();
  return CITY_ALIASES[base] || base;
}
function displayCity(raw: string): string {
  // Best-effort display label — strip diacritics so the alias map's
  // canonical form ("Rome", "Vienna") renders nicely, but preserve
  // capitalisation otherwise. Falls back to the raw city slice when
  // no alias matched.
  const { city } = splitDestination(raw);
  const norm = normCity(raw);
  // If alias kicked in (norm differs from base), surface the English
  // alias capitalised. Otherwise echo the user's input verbatim.
  const base = stripDiacritics(city.toLowerCase());
  if (norm !== base) return norm.charAt(0).toUpperCase() + norm.slice(1);
  return city || raw;
}

// ── Source-type display ──────────────────────────────────────────────
const SOURCE_LABEL: Record<NonNullable<AppTenantTrip['source_type']>, string> = {
  url: 'URL', pdf: 'PDF', word: 'Word', paste: 'Paste',
};

// ── Time-window resolver ─────────────────────────────────────────────
// Returns the [from, to] dates the trip's created_at is filtered
// against. Custom mode reads from customFrom / customTo; the caller
// validates those are non-empty before invoking.
function resolveWindow(
  key:        TimeWindowKey,
  customFrom: string,
  customTo:   string,
): { from: Date; to: Date } | null {
  const now = new Date();
  if (key === 'custom') {
    if (!customFrom || !customTo) return null;
    return { from: new Date(customFrom), to: new Date(`${customTo}T23:59:59`) };
  }
  const days = Number(key);
  if (!Number.isFinite(days) || days <= 0) return null;
  const from = new Date(now);
  from.setDate(from.getDate() - days);
  return { from, to: now };
}

export function TripsPage({ tenant }: { tenant: Tenant }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'planned-live' | string>('planned-live');
  const [trips, setTrips] = useState<AppTenantTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Top Destinations toolbar state.
  const [destOpen, setDestOpen]           = useState(false);
  const [destView, setDestView]           = useState<'city' | 'country'>('city');
  const [timeWindow, setTimeWindow]       = useState<TimeWindowKey>('7');
  const [customFrom, setCustomFrom]       = useState<string>('');
  const [customTo, setCustomTo]           = useState<string>('');
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());

  // Active destination filter — populated by clicking a row in the
  // rollup. Clears via the chip's × or the rollup filter button.
  // Stored as the normalized key so the table filter matches even
  // when the user typed the destination slightly differently.
  const [destFilter, setDestFilter] = useState<{ key: string; label: string; mode: 'city' | 'country' } | null>(null);

  const loadTrips = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTenantTrips(tenant.id);
      setTrips(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrips();
  }, [tenant.id]);

  // Time-windowed trip set. Used by both the Top Destinations rollup
  // AND the trips table — keeping a single window so the rollup
  // numbers reconcile with what the table shows beneath.
  const window = useMemo(
    () => resolveWindow(timeWindow, customFrom, customTo),
    [timeWindow, customFrom, customTo],
  );
  const windowedTrips = useMemo(() => {
    if (!window) return trips;
    const fromMs = window.from.getTime();
    const toMs   = window.to.getTime();
    return trips.filter(t => {
      const created = Date.parse(t.created_at);
      return Number.isFinite(created) && created >= fromMs && created <= toMs;
    });
  }, [trips, window]);

  // Trips left after the user's status + search + destination-rollup
  // click-filter is applied. Drives the bottom table.
  const filtered = useMemo(() => {
    return windowedTrips.filter(t => {
      // Status filter — special handling for the synthetic
      // 'planned-live' preset that lumps active + planning + saved +
      // paused together (per product spec).
      if (filter === 'planned-live') {
        if (!PLANNED_AND_LIVE.has(t.status)) return false;
      } else if (filter !== 'all') {
        if (t.status !== filter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!t.destination_label.toLowerCase().includes(q) && !t.owner_name.toLowerCase().includes(q)) {
          return false;
        }
      }
      if (destFilter) {
        // Match against the normalized destinations. City mode
        // compares city keys; country mode compares country labels
        // (case-insensitive, trimmed).
        const matches = t.destinations.some(d => {
          if (destFilter.mode === 'city') return normCity(d) === destFilter.key;
          const { country } = splitDestination(d);
          return (country?.toLowerCase().trim() ?? '') === destFilter.key;
        });
        if (!matches) return false;
      }
      return true;
    });
  }, [windowedTrips, filter, search, destFilter]);

  const activeTrips    = trips.filter(t => t.status === 'active').length;
  const totalTravelers = trips.reduce((s, t) => s + t.travelers, 0);

  // Status filter options. 'planned-live' is the default and pre-set
  // per product spec — covers active/planning/saved/paused. Other
  // statuses surface only when they actually exist on this tenant's
  // trips, so we don't show an empty 'archived' chip for tenants
  // that have never archived a trip.
  const statusOptions = useMemo(() => {
    const distinct = Array.from(new Set(trips.map(t => t.status)));
    return ['all', 'planned-live', ...distinct.filter(s => !PLANNED_AND_LIVE.has(s))];
  }, [trips]);

  // ── Top Destinations rollup ────────────────────────────────────────
  // For each trip in the window, count every (normalized) destination
  // it touches once. Aggregate: trip count, planned/imported split,
  // unique users, last-activity timestamp. City view groups by city;
  // country view groups by country with city sub-rows we can expand.
  const rollup = useMemo(() => {
    interface Slot {
      key:           string;     // normalized city OR lowercased country
      display:       string;     // user-facing label
      country:       string | null;
      tripCount:     number;
      plannedCount:  number;
      importedCount: number;
      userIds:       Set<string>;
      lastActivity:  number;     // ms timestamp
      cities?:       Map<string, Omit<Slot, 'cities'>>; // only for country mode
    }
    const cityMap    = new Map<string, Slot>();
    const countryMap = new Map<string, Slot>();

    for (const t of windowedTrips) {
      // Honour the "Planned + Live" status filter on the rollup too,
      // EVEN when the table-level status filter is different — the
      // rollup is always "what are people actively planning?"
      if (!PLANNED_AND_LIVE.has(t.status)) continue;

      const isPlanned = t.source_type === null;
      const isImported = !isPlanned;
      const tripMs = Date.parse(t.created_at);
      // Dedupe destinations within the same trip so a single trip
      // listing "Paris, Paris, France" doesn't double-count.
      const seenCities    = new Set<string>();
      const seenCountries = new Set<string>();
      for (const d of t.destinations) {
        const cityKey = normCity(d);
        if (!cityKey || seenCities.has(cityKey)) continue;
        seenCities.add(cityKey);
        const { country } = splitDestination(d);
        const countryKey = country?.toLowerCase().trim() ?? '';

        // City slot
        const existing = cityMap.get(cityKey) ?? {
          key:           cityKey,
          display:       displayCity(d),
          country:       country,
          tripCount:     0, plannedCount: 0, importedCount: 0,
          userIds:       new Set<string>(),
          lastActivity:  0,
        };
        existing.tripCount     += 1;
        existing.plannedCount  += isPlanned ? 1 : 0;
        existing.importedCount += isImported ? 1 : 0;
        existing.userIds.add(t.owner_email || t.owner_name);
        if (tripMs > existing.lastActivity) existing.lastActivity = tripMs;
        cityMap.set(cityKey, existing);

        // Country slot (only if we actually parsed a country)
        if (countryKey && !seenCountries.has(countryKey)) {
          seenCountries.add(countryKey);
          const cExisting = countryMap.get(countryKey) ?? {
            key:           countryKey,
            display:       country!,
            country:       null,
            tripCount:     0, plannedCount: 0, importedCount: 0,
            userIds:       new Set<string>(),
            lastActivity:  0,
            cities:        new Map(),
          };
          cExisting.tripCount     += 1;
          cExisting.plannedCount  += isPlanned ? 1 : 0;
          cExisting.importedCount += isImported ? 1 : 0;
          cExisting.userIds.add(t.owner_email || t.owner_name);
          if (tripMs > cExisting.lastActivity) cExisting.lastActivity = tripMs;
          // Track per-city stats inside this country for the expand
          // sub-rows. We re-add the city row here so the country
          // expand shows the same counts the standalone city row
          // would show — just scoped to this country.
          const cityInCountry = cExisting.cities!.get(cityKey) ?? {
            key:           cityKey,
            display:       displayCity(d),
            country:       country,
            tripCount:     0, plannedCount: 0, importedCount: 0,
            userIds:       new Set<string>(),
            lastActivity:  0,
          };
          cityInCountry.tripCount     += 1;
          cityInCountry.plannedCount  += isPlanned ? 1 : 0;
          cityInCountry.importedCount += isImported ? 1 : 0;
          cityInCountry.userIds.add(t.owner_email || t.owner_name);
          if (tripMs > cityInCountry.lastActivity) cityInCountry.lastActivity = tripMs;
          cExisting.cities!.set(cityKey, cityInCountry);
          countryMap.set(countryKey, cExisting);
        }
      }
    }

    // Convert to sorted arrays. Both views sort by trip count desc
    // (most popular first); tie-breaks by most-recent activity.
    const sortFn = (a: Slot, b: Slot) =>
      (b.tripCount - a.tripCount) || (b.lastActivity - a.lastActivity);
    const cities    = Array.from(cityMap.values()).sort(sortFn);
    const countries = Array.from(countryMap.values()).sort(sortFn);

    // Total counts for the header chip — count distinct destinations,
    // not trip rows, so the chip reads "47 unique places" not "112
    // place mentions across trips."
    const distinctCount = destView === 'country' ? countries.length : cities.length;
    const tripsInWindow = windowedTrips.filter(t => PLANNED_AND_LIVE.has(t.status)).length;

    return { cities, countries, distinctCount, tripsInWindow };
  }, [windowedTrips, destView]);

  // Format helpers shared between the rollup rows and the table.
  const formatRel = (ms: number): string => {
    if (!ms) return '—';
    const secs = Math.max(1, Math.floor((Date.now() - ms) / 1000));
    if (secs < 60)      return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60)      return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24)     return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30)      return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12)    return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Trips</h1>
          <p className="text-sm text-surface-500 mt-1">All trips created by {tenant.name}'s users</p>
        </div>
        <button onClick={loadTrips} className="btn-secondary text-sm" title="Refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Trips" value={trips.length} icon={<MapPin size={18} />} delay={0} />
        <StatCard label="Active Now" value={activeTrips} change="in progress" changeType="neutral" icon={<Calendar size={18} />} delay={50} />
        <StatCard label="Total Travelers" value={totalTravelers} icon={<Users size={18} />} delay={100} />
      </div>

      {/* ── Top Destinations card ─────────────────────────────────── */}
      {/* Foldable; header always visible with the unique-places counter.
          Sits above the search bar so the rollup acts as a quick "where
          is interest concentrated?" read before the user dives into
          the trip list. */}
      <div className="glass-panel p-5">
        <button
          type="button"
          onClick={() => setDestOpen(o => !o)}
          className="w-full flex items-center justify-between gap-3 text-left"
          aria-expanded={destOpen}
        >
          <div className="flex items-center gap-2 min-w-0">
            <ChevronDown
              size={14}
              className={`text-surface-500 shrink-0 transition-transform ${destOpen ? '' : '-rotate-90'}`}
            />
            <MapPin size={16} className="text-brand-500 shrink-0" />
            <h3 className="text-sm font-semibold text-surface-900 truncate">Top Destinations</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase tracking-wider shrink-0">
              {rollup.distinctCount} unique {destView === 'country' ? 'countries' : 'cities'} · {rollup.tripsInWindow} trips · {WINDOW_LABEL[timeWindow]}
            </span>
          </div>
        </button>

        {destOpen && (
          <div className="mt-4 space-y-3">
            {/* Toolbar — view toggle + time window picker + custom range
                inputs that show only when timeWindow === 'custom'. */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex rounded-md border border-surface-200 overflow-hidden text-xs">
                <button
                  type="button"
                  onClick={() => setDestView('city')}
                  className={`px-2.5 py-1 font-medium transition-colors ${destView === 'city' ? 'bg-brand-500 text-white' : 'bg-white text-surface-700 hover:bg-surface-50'}`}
                >City</button>
                <button
                  type="button"
                  onClick={() => setDestView('country')}
                  className={`px-2.5 py-1 font-medium transition-colors border-l border-surface-200 ${destView === 'country' ? 'bg-brand-500 text-white' : 'bg-white text-surface-700 hover:bg-surface-50'}`}
                >Country</button>
              </div>

              <label className="flex items-center gap-1.5 text-xs text-surface-600">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Window</span>
                <select
                  value={timeWindow}
                  onChange={(e) => setTimeWindow(e.target.value as TimeWindowKey)}
                  className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                >
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                  <option value="custom">Custom</option>
                </select>
              </label>

              {timeWindow === 'custom' && (
                <div className="inline-flex items-center gap-2 text-xs text-surface-600">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                    aria-label="From date"
                  />
                  <span>→</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                    aria-label="To date"
                  />
                </div>
              )}
            </div>

            {/* Rows. City view = flat list. Country view = expandable
                country rows with per-city sub-rows. Both share the
                same row component shape; sub-rows are rendered with
                a left indent when their parent is expanded. */}
            {destView === 'city' ? (
              <div className="space-y-1.5">
                {rollup.cities.length === 0 && (
                  <div className="text-xs text-surface-500 py-3 text-center">No destinations in this window.</div>
                )}
                {rollup.cities.slice(0, 50).map((row, idx) => (
                  <DestinationRow
                    key={row.key}
                    rank={idx + 1}
                    label={row.display}
                    secondary={row.country || ''}
                    trips={row.tripCount}
                    planned={row.plannedCount}
                    imported={row.importedCount}
                    users={row.userIds.size}
                    lastActivity={formatRel(row.lastActivity)}
                    active={destFilter?.mode === 'city' && destFilter.key === row.key}
                    onClick={() =>
                      setDestFilter(prev =>
                        prev?.mode === 'city' && prev.key === row.key
                          ? null
                          : { mode: 'city', key: row.key, label: row.display },
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                {rollup.countries.length === 0 && (
                  <div className="text-xs text-surface-500 py-3 text-center">No countries in this window.</div>
                )}
                {rollup.countries.slice(0, 50).map((row, idx) => {
                  const isExpanded = expandedCountries.has(row.key);
                  return (
                    <div key={row.key}>
                      <DestinationRow
                        rank={idx + 1}
                        label={row.display}
                        secondary={`${row.cities?.size || 0} cities`}
                        trips={row.tripCount}
                        planned={row.plannedCount}
                        imported={row.importedCount}
                        users={row.userIds.size}
                        lastActivity={formatRel(row.lastActivity)}
                        active={destFilter?.mode === 'country' && destFilter.key === row.key}
                        expandable={(row.cities?.size || 0) > 0}
                        expanded={isExpanded}
                        onToggleExpand={() => {
                          setExpandedCountries(prev => {
                            const next = new Set(prev);
                            if (next.has(row.key)) next.delete(row.key);
                            else next.add(row.key);
                            return next;
                          });
                        }}
                        onClick={() =>
                          setDestFilter(prev =>
                            prev?.mode === 'country' && prev.key === row.key
                              ? null
                              : { mode: 'country', key: row.key, label: row.display },
                          )
                        }
                      />
                      {isExpanded && row.cities && (
                        <div className="pl-8 mt-1 space-y-1.5">
                          {Array.from(row.cities.values())
                            .sort((a, b) => b.tripCount - a.tripCount)
                            .map(sub => (
                              <DestinationRow
                                key={sub.key}
                                rank={null}
                                label={sub.display}
                                secondary=""
                                trips={sub.tripCount}
                                planned={sub.plannedCount}
                                imported={sub.importedCount}
                                users={sub.userIds.size}
                                lastActivity={formatRel(sub.lastActivity)}
                                active={destFilter?.mode === 'city' && destFilter.key === sub.key}
                                onClick={() =>
                                  setDestFilter(prev =>
                                    prev?.mode === 'city' && prev.key === sub.key
                                      ? null
                                      : { mode: 'city', key: sub.key, label: sub.display },
                                  )
                                }
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Search + status filter ──────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search trips..." className="input-field pl-10 py-2" />
        </div>
        <div className="flex gap-1">
          {statusOptions.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f ? 'badge-brand border border-brand-500/20' : 'text-surface-600 hover:bg-surface-100'}`}>
              {f === 'planned-live' ? 'Planned + Live' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Click-to-filter chip — shows the active destination filter
            from the Top Destinations card. × clears it. */}
        {destFilter && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">
            <MapPin size={12} />
            {destFilter.mode === 'country' ? 'Country: ' : ''}{destFilter.label}
            <button
              type="button"
              onClick={() => setDestFilter(null)}
              className="ml-1 hover:text-brand-900"
              aria-label="Clear destination filter"
            >
              <X size={12} />
            </button>
          </span>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="glass-panel p-6 text-center">
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
          <button onClick={loadTrips} className="btn-secondary text-sm">Retry</button>
        </div>
      )}

      {/* Loading state */}
      {loading && !error && (
        <div className="glass-panel p-12 flex items-center justify-center">
          <Spinner size="md" />
          <span className="ml-3 text-sm text-surface-500">Loading trips...</span>
        </div>
      )}

      {/* Trips table */}
      {!loading && !error && (
        <div className="glass-panel overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell">Destination</th>
                <th className="table-header-cell">Type</th>
                <th className="table-header-cell">Owner</th>
                <th className="table-header-cell">Travelers</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-surface-500">
                  {trips.length === 0 ? 'No trips have been created yet.' : 'No trips match these filters.'}
                </td></tr>
              ) : filtered.map(trip => (
                <tr key={trip.id} className="table-row">
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-brand-500 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-surface-900">{trip.destination_label}</div>
                        <div className="text-xs text-surface-400 font-mono">{trip.id.slice(0, 8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <TripTypeChip trip={trip} />
                  </td>
                  <td className="table-cell">
                    <div className="text-sm text-surface-700">{trip.owner_name}</div>
                    <div className="text-xs text-surface-500">{trip.owner_email}</div>
                  </td>
                  <td className="table-cell text-surface-700">{trip.travelers}</td>
                  <td className="table-cell">
                    <TripStatus status={trip.status} />
                  </td>
                  <td className="table-cell text-surface-500 text-xs">
                    {new Date(trip.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── DestinationRow ──────────────────────────────────────────────────
// Single row in the Top Destinations rollup. Used for both flat city
// rows and country header rows; sub-rows reuse it with rank=null so
// the indent column collapses cleanly. Click toggles the destination
// filter on the table below; the active state lifts the row with a
// brand-tinted background.
function DestinationRow({
  rank, label, secondary, trips, planned, imported, users, lastActivity,
  active, expandable, expanded, onClick, onToggleExpand,
}: {
  rank:           number | null;
  label:          string;
  secondary:      string;
  trips:          number;
  planned:        number;
  imported:       number;
  users:          number;
  lastActivity:   string;
  active:         boolean;
  expandable?:    boolean;
  expanded?:      boolean;
  onClick:        () => void;
  onToggleExpand?: () => void;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
        active ? 'bg-brand-50 ring-1 ring-brand-200' : 'hover:bg-surface-50'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      {expandable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
          className="text-surface-500 hover:text-surface-900 shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      )}
      {rank !== null && (
        <span className="w-6 text-right text-[10px] font-bold text-surface-400 tabular-nums shrink-0">
          #{rank}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-surface-900 font-medium truncate">{label}</div>
        {secondary && (
          <div className="text-[10px] text-surface-500 truncate">{secondary}</div>
        )}
      </div>
      <div className="text-xs text-surface-700 tabular-nums shrink-0">
        <span className="font-bold text-surface-900">{trips}</span> trips
        <span className="text-surface-500 text-[10px]"> · {planned}p · {imported}i</span>
      </div>
      <div className="text-xs text-surface-500 tabular-nums w-16 text-right shrink-0">
        {users} {users === 1 ? 'user' : 'users'}
      </div>
      <div className="text-[10px] text-surface-400 w-20 text-right shrink-0">
        {lastActivity}
      </div>
    </div>
  );
}

// ── TripTypeChip ────────────────────────────────────────────────────
// Renders a Planned (purple) or Imported · SOURCE (blue) chip in the
// Type column. URL imports add a small link-out icon that opens the
// original source_url in a new tab so the admin can see the source.
function TripTypeChip({ trip }: { trip: AppTenantTrip }) {
  if (trip.source_type === null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold uppercase tracking-wider">
        <Sparkles size={10} />
        Planned
      </span>
    );
  }
  const label = SOURCE_LABEL[trip.source_type];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-wider">
        <Upload size={10} />
        Imported · {label}
      </span>
      {trip.source_type === 'url' && trip.source_url && (
        <a
          href={trip.source_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-blue-500 hover:text-blue-700"
          aria-label="Open original URL"
          title={trip.source_url}
        >
          <ExternalLink size={12} />
        </a>
      )}
    </span>
  );
}

function TripStatus({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'badge-emerald',
    planning: 'badge-blue',
    saved: 'badge-blue',
    paused: 'badge-amber',
    completed: 'bg-surface-100 text-surface-600',
    archived: 'bg-surface-100 text-surface-500',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.saved}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
